import React, { useEffect, useState } from 'react';
import { Box, Typography, Container, Paper, Button } from '@mui/material';
import usePageTitle from '../../hooks/usePageTitle';
import PublicPageLayout from '../../components/layout/PublicPageLayout';
import CanonicalLink from '../../components/common/CanonicalLink';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';
import { parseMarkdownWithFrontmatter, formatLegalDate } from '../../utils/markdownLoader';

// Import the markdown content as a string
import privacyPolicyMarkdown from '../../content/legal/privacy-policy.md';

const PrivacyPolicyPage: React.FC = () => {
  const [content, setContent] = useState<string>('');
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  // Set the page title
  usePageTitle('Privacy Policy - WaveScoop', false);

  // Load and parse markdown content
  useEffect(() => {
    try {
      // The markdown is already loaded as a string by raw-loader
      const { content: markdownContent, metadata } = parseMarkdownWithFrontmatter(privacyPolicyMarkdown);
      
      setContent(markdownContent);
      setLastUpdated(formatLegalDate(metadata.lastUpdated));
      setIsLoading(false);
    } catch (error) {
      console.error('Error parsing privacy policy:', error);
      setIsLoading(false);
    }
  }, []);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Function to reopen cookie banner
  const reopenCookieBanner = () => {
    // Clear the current consent to force the banner to reappear
    localStorage.removeItem("wavescoop_cookie_consent");
    window.location.reload();
  };

  if (isLoading) {
    return (
      <PublicPageLayout>
        <Container maxWidth="lg">
          <Box sx={{ my: 4 }}>
            <Typography>Loading...</Typography>
          </Box>
        </Container>
      </PublicPageLayout>
    );
  }

  return (
    <PublicPageLayout>
      <CanonicalLink />
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Typography variant="h2" component="h1" gutterBottom sx={{ color: 'white' }}>
            WaveScoop Privacy Policy
          </Typography>
          {lastUpdated && (
            <Typography variant="subtitle1" paragraph sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              Last updated: {lastUpdated}
            </Typography>
          )}
        </Box>

        <Paper 
          elevation={3} 
          sx={{ 
            p: { xs: 2, sm: 3, md: 4 },
            mb: 4,
            bgcolor: 'rgba(10, 30, 60, 0.85)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <MarkdownRenderer content={content} />
          
          {/* Cookie Settings Button */}
          <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Cookie Settings
            </Typography>
            <Typography paragraph sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              You can manage your cookie preferences at any time by clicking the button below.
            </Typography>
            <Button 
              variant="outlined"
              onClick={reopenCookieBanner}
              sx={{
                color: '#64b5f6',
                borderColor: '#64b5f6',
                '&:hover': {
                  borderColor: '#90caf9',
                  backgroundColor: 'rgba(100, 181, 246, 0.08)'
                }
              }}
            >
              Manage Cookie Preferences
            </Button>
          </Box>
        </Paper>
      </Container>
    </PublicPageLayout>
  );
};

export default PrivacyPolicyPage;